import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { API_SERVICE } from '@/components/candidate-job-slider-v1/components/ApplicationIntelligence/api';
import { APPLICATION_SCORECARD_API } from '@/components/contacts/candidate-job-drawer/tabs/application-scorecard/api';
import {
  defaultApplicationScorecardSort,
  mapSortOptionToApiParams,
  sortScorecards,
  transformScorecardsToTableFormat,
} from '@/components/contacts/candidate-job-drawer/tabs/application-scorecard/helper';
import { http } from '@/lib/ats-https';
import { http as mainHttp } from '@/lib/http';
import type { GetApplicationScorecardsParams } from '@/components/contacts/candidate-job-drawer/tabs/application-scorecard/api';
import {
  ScorecardItem,
  ScorecardPageParams,
} from '../GenerateScorecard.interface';

// ============================================================
// LOCAL DEVELOPMENT - Set app ID to test with real APIs locally
// Set to null for production flow (requires ?id= param in URL)
// ============================================================
const MOCK_APPLICATION_ID: number | null = 129853;

// ============================================================

function getQueryParams(url: string): ScorecardPageParams {
  const params = new URLSearchParams(new URL(url).search);
  const result: ScorecardPageParams = {};

  if (params.get('token')) {
    result.token = params.get('token') || undefined;
  }
  if (params.get('ats_application_id')) {
    result.ats_application_id = params.get('ats_application_id') || undefined;
  }
  if (params.get('id')) {
    result.id = params.get('id') || undefined;
  }
  if (params.get('sort')) {
    result.sort = params.get('sort') || undefined;
  }
  if (params.get('tz')) {
    result.tz = params.get('tz') || undefined;
  }
  if (params.get('date_time_format')) {
    result.date_time_format = params.get('date_time_format') || undefined;
  }

  return result;
}

interface ApplicationData {
  ats_job: {
    id: number;
    name: string;
  } | null;
  contact: {
    id: number;
    name: string;
    avatar: string | null;
  } | null;
  /** Pipeline order for stage sort (same as application scorecard drawer) */
  ats_job_stages?: unknown[];
}

interface CreatedBy {
  id: number;
  name: string;
  avatar: string | null;
}

/** First non-empty image URL, or null (Avatar will show initials from `name`). */
function pickAvatarUrl(
  entity:
    | {
        avatar?: string | null;
        avatar_url?: string | null;
        image_url?: string | null;
      }
    | null
    | undefined
): string | null {
  if (!entity) return null;
  const raw = entity.avatar ?? entity.avatar_url ?? entity.image_url;
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s || null;
}

interface SavedReportPayload {
  query?: { ats_application_id?: number; sort?: string };
  user?: {
    id: number;
    name?: string | null;
    avatar?: string | null;
    avatar_url?: string | null;
    image_url?: string | null;
  };
}

interface ApplicationShowPayload {
  ats_job?: { id: number; name: string };
  contact?: {
    id: number;
    name?: string | null;
    avatar?: string | null;
    avatar_url?: string | null;
    image_url?: string | null;
  };
  ats_job_stages?: unknown[];
}

interface SavedReportData {
  atsApplicationId: number | null;
  user: CreatedBy | null;
  sort: string | null;
}

interface UserPrefs {
  time_zone: string | null;
  date_fmt: string | null;
  time_fmt: string | null;
}

interface UseGenerateScorecardApiReturn {
  loading: boolean;
  error: string | null;
  scorecards: ScorecardItem[];
  params: ScorecardPageParams;
  applicationData: ApplicationData | null;
  createdBy: CreatedBy | null;
  aiSummary: string | null;
  userPrefs: UserPrefs | null;
}

// API functions
const fetchUserPrefs = async (): Promise<UserPrefs> => {
  const body = (await mainHttp.get('/me')) as { data?: UserPrefs };
  const data = body?.data;
  return {
    time_zone: data?.time_zone ?? null,
    date_fmt: data?.date_fmt ?? null,
    time_fmt: data?.time_fmt ?? null,
  };
};

const fetchSavedReport = async (reportId: string): Promise<SavedReportData> => {
  const body = (await http.get(`reports/saved_reports/${reportId}`)) as {
    data?: SavedReportPayload;
  };
  const reportData = body?.data;

  return {
    atsApplicationId: reportData?.query?.ats_application_id ?? null,
    user: reportData?.user
      ? {
          id: reportData.user.id,
          name: reportData.user.name?.trim() ?? '',
          avatar: pickAvatarUrl(reportData.user),
        }
      : null,
    sort: reportData?.query?.sort ?? null,
  };
};

const fetchApplicationData = async (
  applicationId: number
): Promise<ApplicationData> => {
  const body = (await http.get(`ats_applications/${applicationId}`)) as {
    data?: ApplicationShowPayload;
  };
  const appData = body?.data;

  return {
    ats_job: appData?.ats_job
      ? { id: appData.ats_job.id, name: appData.ats_job.name }
      : null,
    contact: appData?.contact
      ? {
          id: appData.contact.id,
          name: appData.contact.name?.trim() ?? '',
          avatar: pickAvatarUrl(appData.contact),
        }
      : null,
    ats_job_stages: appData?.ats_job_stages ?? [],
  };
};

// PDF sort mapping — “most_recent” uses order=asc (differs from the shared helper).
const PDF_SORT_MAP: Record<string, { sort_by: string; order: string }> = {
  stage: { sort_by: 'stage', order: 'asc' },
  most_recent: { sort_by: 'activity_time', order: 'asc' },
  rating_highest_first: { sort_by: 'rating', order: 'desc' },
  rating_lowest_first: { sort_by: 'rating', order: 'asc' },
};

const fetchScorecardsForPdf = async (
  applicationId: number,
  sortKey?: string
): Promise<ScorecardItem[]> => {
  const key = sortKey || defaultApplicationScorecardSort.value;
  const sortParams = (PDF_SORT_MAP[key] ?? PDF_SORT_MAP.stage) as
    | GetApplicationScorecardsParams
    | undefined;
  const result = await APPLICATION_SCORECARD_API.getApplicationScorecards(
    applicationId,
    ['submitted'],
    sortParams
  );
  const payload =
    (result as { data?: { scorecards?: ScorecardItem[] } })?.data ?? result;
  return (payload as { scorecards?: ScorecardItem[] })?.scorecards ?? [];
};

export const useGenerateScorecardApi = (): UseGenerateScorecardApiReturn => {
  const { setAuth } = useAuth();
  const { feApplicationAssistant, aiSummaryPrompt } = useKFlags();

  const params = useMemo(() => getQueryParams(window.location.href), []);
  const [isAuthReady, setIsAuthReady] = useState(!params.token);

  // Token exchange effect - only needed when token param exists
  useEffect(() => {
    const handleTokenExchange = async () => {
      if (!params.token) {
        setIsAuthReady(true);
        return;
      }

      const baseurl = window.location.hostname;
      const url = `https://${baseurl}/api/v1/auth/exchange`;

      try {
        const response = await axios.post(url, { token: params.token });
        const token = response?.data?.data?.access_token;
        setAuth(token);
        localStorage.setItem('apiv1Auth', token);
        setIsAuthReady(true);
      } catch (err) {
        console.error('Error during token exchange:', err);
        setIsAuthReady(true); // Still set ready to show error state
      }
    };

    handleTokenExchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.token]);

  // Use mock application ID for local dev, or fetch from saved report
  const shouldUseMockAppId = MOCK_APPLICATION_ID !== null && !params.id;

  // Query: Fetch current user's date/time/timezone preferences after auth is ready.
  // Bootstrap runs before token exchange so Redux currUser is unreliable in Gotenberg;
  // this fetch happens after isAuthReady to get accurate values.
  const userPrefsQuery = useQuery({
    queryKey: ['scorecardPdfUserPrefs'],
    queryFn: fetchUserPrefs,
    enabled: isAuthReady,
    staleTime: 0,
  });

  // Query: Fetch saved report to get ats_application_id and user
  const savedReportQuery = useQuery({
    queryKey: ['savedReport', params.id],
    queryFn: () => fetchSavedReport(params.id!),
    enabled: isAuthReady && !!params.id && !shouldUseMockAppId,
    staleTime: 0,
  });

  const atsApplicationId = shouldUseMockAppId
    ? MOCK_APPLICATION_ID
    : savedReportQuery.data?.atsApplicationId;

  // Query: Fetch application data (dependent on saved report or mock app ID)
  const applicationQuery = useQuery({
    queryKey: ['applicationData', atsApplicationId],
    queryFn: () => fetchApplicationData(atsApplicationId!),
    enabled: isAuthReady && !!atsApplicationId,
    staleTime: 0,
  });

  // Priority: URL param (?sort=most_recent) > saved report > default (stage)
  const pdfScorecardSortKey =
    params.sort ||
    savedReportQuery.data?.sort ||
    defaultApplicationScorecardSort.value;

  // Query: Fetch scorecards — view/limit/filter/sort match application scorecard API
  const scorecardsQuery = useQuery({
    queryKey: ['scorecards', 'pdf', atsApplicationId, pdfScorecardSortKey],
    queryFn: () =>
      fetchScorecardsForPdf(atsApplicationId!, pdfScorecardSortKey),
    enabled: isAuthReady && !!atsApplicationId,
    staleTime: 0,
  });

  // Query: AI summary (same flow as Application Intelligence short summary)
  const aiSummaryQuery = useQuery({
    queryKey: ['scorecardPdfAiSummary', atsApplicationId, aiSummaryPrompt],
    queryFn: async () => {
      try {
        const response = await API_SERVICE.getSummary(
          String(atsApplicationId!),
          aiSummaryPrompt ?? ''
        );
        if (response?.data?.status === 'completed' && response?.data?.summary) {
          return response.data.summary as string;
        }
      } catch {
        // Summary is optional for PDF; avoid failing the page
      }
      return null;
    },
    enabled: isAuthReady && !!atsApplicationId && !!feApplicationAssistant,
    staleTime: 0,
  });

  // Compute loading state
  const loading =
    !isAuthReady ||
    userPrefsQuery.isLoading ||
    (!shouldUseMockAppId && !!params.id && savedReportQuery.isLoading) ||
    (!!atsApplicationId && applicationQuery.isLoading) ||
    (!!atsApplicationId && scorecardsQuery.isLoading);

  // Compute error state
  const error =
    !shouldUseMockAppId && savedReportQuery.error
      ? 'Failed to load report data'
      : applicationQuery.error
        ? 'Failed to load application data'
        : scorecardsQuery.error
          ? 'Failed to load scorecard data'
          : null;

  const createdBy = savedReportQuery?.data?.user || null;
  const applicationData = applicationQuery.data || null;
  const aiSummary = aiSummaryQuery.data || null;

  // Transform raw API data for field mapping (reviewer.avatar, stage info, etc.)
  // but preserve the BE sort order — the API already returns data sorted by the
  // requested sort_by/order params, so no client-side re-sort is needed.
  const scorecards = useMemo(() => {
    const raw = scorecardsQuery.data;
    if (!raw?.length) return [];
    return transformScorecardsToTableFormat(raw, {
      ats_job_stages: applicationData?.ats_job_stages ?? [],
    }) as ScorecardItem[];
  }, [scorecardsQuery.data, applicationData?.ats_job_stages]);

  return {
    loading,
    error,
    scorecards,
    params,
    applicationData,
    createdBy,
    aiSummary,
    userPrefs: userPrefsQuery.data ?? null,
  };
};
